import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    Box,
    CircularProgress,
    Grid,
    TextField,
} from '@mui/material';
import { useNotify, usePermissions } from 'react-admin';
import {
    getLeaveBalances,
    getAllLeaveBalances,
    checkPermission,
    exportLeaveBalances,
    LeaveBalance,
    CompanyLeaveBalance,
} from '../dataProvider';
import { ImportBalancesDialog } from './ImportBalancesDialog';
import { ImportRequestsDialog } from './ImportRequestsDialog';
import { AdjustBalanceDialog } from './AdjustBalanceDialog';

export const TimeoffBalance: React.FC = () => {
    const navigate = useNavigate();
    const notify = useNotify();
    const { permissions } = usePermissions<string[]>();

    const [myBalances, setMyBalances] = useState<LeaveBalance[]>([]);
    const [companyBalances, setCompanyBalances] = useState<CompanyLeaveBalance[]>([]);
    const [myLoading, setMyLoading] = useState(true);
    const [companyLoading, setCompanyLoading] = useState(false);
    const [canViewCompany, setCanViewCompany] = useState(false);
    const [permissionChecked, setPermissionChecked] = useState(false);
    const [nameFilter, setNameFilter] = useState('');
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [importRequestsDialogOpen, setImportRequestsDialogOpen] = useState(false);
    const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);

    const canExport = Array.isArray(permissions) && permissions.includes('timeoff.export');
    const canImport = Array.isArray(permissions) && permissions.includes('timeoff.import');

    const handleExport = async () => {
        try {
            await exportLeaveBalances();
        } catch (error: any) {
            const errorMessage = error?.body?.message || error?.message || 'Export failed';
            notify(errorMessage, { type: 'error' });
        }
    };

    useEffect(() => {
        const fetchMyBalances = async () => {
            setMyLoading(true);
            try {
                const data = await getLeaveBalances();
                setMyBalances(data);
            } catch (_error) {
                notify('Failed to load your leave balances', { type: 'error' });
            } finally {
                setMyLoading(false);
            }
        };

        const checkCompanyPermission = async () => {
            const permitted = await checkPermission('timeoff.view_all_balances');
            setCanViewCompany(permitted);
            setPermissionChecked(true);
        };

        fetchMyBalances();
        checkCompanyPermission();
    }, []);

    useEffect(() => {
        if (!permissionChecked || !canViewCompany) return;

        const fetchCompanyBalances = async () => {
            setCompanyLoading(true);
            try {
                const data = await getAllLeaveBalances();
                setCompanyBalances(data);
            } catch (_error) {
                notify('Failed to load company leave balances', { type: 'error' });
            } finally {
                setCompanyLoading(false);
            }
        };

        fetchCompanyBalances();
    }, [permissionChecked, canViewCompany]);

    const getRemaining = (balance: LeaveBalance) =>
        balance.allocated_days - balance.used_days;

    const filteredCompanyBalances = companyBalances.filter((b) =>
        b.full_name?.toLowerCase().includes(nameFilter.toLowerCase())
    );

    if (myLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5">Time-Off Balance</Typography>
                <Box display="flex" gap={1}>
                    {canExport && (
                        <Button variant="outlined" onClick={handleExport}>
                            Export Balances
                        </Button>
                    )}
                    {canImport && (
                        <Button variant="outlined" onClick={() => setImportDialogOpen(true)}>
                            Import Balances
                        </Button>
                    )}
                    {canImport && (
                        <Button variant="outlined" onClick={() => setImportRequestsDialogOpen(true)}>
                            Import Requests
                        </Button>
                    )}
                    {canImport && (
                        <Button variant="outlined" onClick={() => setAdjustDialogOpen(true)}>
                            Adjust Balance
                        </Button>
                    )}
                    <Button variant="outlined" onClick={() => navigate('/timeoff')}>
                        Back to My Requests
                    </Button>
                </Box>
            </Box>

            <Typography variant="h6" gutterBottom>
                My Balance
            </Typography>

            {myBalances.length === 0 ? (
                <Typography color="text.secondary" mb={3}>
                    No leave balances found.
                </Typography>
            ) : (
                <Grid container spacing={2} mb={3}>
                    {myBalances.map((balance) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={balance.leave_type}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6">{balance.leave_type} Leave</Typography>
                                    <Box display="flex" flexDirection="column" gap={1} mt={1}>
                                        <Typography>
                                            Allocated: <strong>{balance.allocated_days}</strong> days
                                        </Typography>
                                        <Typography>
                                            Used: <strong>{balance.used_days}</strong> days
                                        </Typography>
                                        {balance.leave_type === 'Annual' && (
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography component="span">Remaining:</Typography>
                                                <Chip
                                                    label={`${getRemaining(balance)} days`}
                                                    color={getRemaining(balance) > 0 ? 'success' : 'error'}
                                                    size="small"
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {canViewCompany && (
                <>
                    <Typography variant="h6" gutterBottom>
                        Company Balance
                    </Typography>

                    <Box mb={2}>
                        <TextField
                            label="Filter by employee name"
                            variant="outlined"
                            size="small"
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                            sx={{ width: 320 }}
                        />
                    </Box>

                    {companyLoading ? (
                        <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Employee Name</TableCell>
                                        <TableCell>Leave Type</TableCell>
                                        <TableCell>Allocated</TableCell>
                                        <TableCell>Used</TableCell>
                                        <TableCell>Remaining</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredCompanyBalances.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                No company balance data found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredCompanyBalances.map((balance, index) => (
                                            <TableRow
                                                key={`${balance.employee_id}-${balance.leave_type}-${index}`}
                                            >
                                                <TableCell>{balance.full_name}</TableCell>
                                                <TableCell>{balance.leave_type}</TableCell>
                                                <TableCell>{balance.allocated_days}</TableCell>
                                                <TableCell>{balance.used_days}</TableCell>
                                                <TableCell>
                                                    {balance.allocated_days - balance.used_days}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )}

            <ImportBalancesDialog
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
            />
            <ImportRequestsDialog
                open={importRequestsDialogOpen}
                onClose={() => setImportRequestsDialogOpen(false)}
            />
            <AdjustBalanceDialog
                open={adjustDialogOpen}
                onClose={() => setAdjustDialogOpen(false)}
            />
        </Box>
    );
};
